// src/InteriorPage.js
import React, { useState } from 'react';
import { dedupeChecklist } from './utils/dedupeChecklist';


export default function InteriorPage({ checklist, images }) {
  const colorBlue = '#153BC6';
  const lightGray1 = '#f7f7f7';
  const lightGray2 = '#eaeaea';

  /* -------- state for the image modal (unchanged) -------- */
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  /* -------- helper to scroll to the Damage Report -------- */
  function scrollToDamageReport() {
    // Prefer the interior section anchor; fall back to the page top
    const target =
      document.getElementById('damage-interior') ||
      document.getElementById('damage-interior-damage') || // alt. text
      document.getElementById('damage-report');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* -------- render helpers (icon & images) --------------- */
  function renderDamageIcon(severity) {
    const baseStyle = {
      display: 'inline-block',
      verticalAlign: 'middle',
    };

    if (severity === 'Light') {
      // use the MagnifyingGlass.png from public/
      return (
        <img
          src="/MagnifyingGlass.png"
          alt="Inspect"
          style={{
            ...baseStyle,
            width: '1.4rem',
            height: '1.4rem',
          }}
        />
      );
    }

    if (severity === 'Moderate') {
      return <span style={{ ...baseStyle, color: 'orange', fontSize: '1.4rem' }}>&#9888;</span>;
    }
    if (severity === 'Severe') {
      return <span style={{ ...baseStyle, color: 'red', fontSize: '1.8rem' }}>&#10007;</span>;
    }
    return <span style={{ ...baseStyle, color: 'green', fontSize: '1.4rem' }}>&#10003;</span>;
  }
  
  // All the interior image keys
  const interiorImageKeys = [
    'FullDashboardImage',
    'CenterStackImage',
    'GaugeClusterImage',
    'OdometerImage',
    'RightFrontInteriorImage',
    'LeftFrontInteriorImage',
    'RightRearInteriorImage',
    'LeftRearInteriorImage',
    'TrunkTailgateHatchOpenImage',
    'ThirdRowSeatsUpImage',
    'ThirdRowSeatsDownImage',
    'VinStickerImage',
    'VinPlateImage',
    'EvChargingCableImage',
    'KeysManualImage',
  ];

  // Build an array of actual image URLs
  const interiorUrls = interiorImageKeys
    .map((key) => images[key])
    .filter(Boolean);

  function openModal(idx) {
    setSelectedIndex(idx);
    setZoomed(false);
  }

  function closeModal() {
    setSelectedIndex(null);
    setZoomed(false);
  }

  function toggleZoom() {
    setZoomed((prev) => !prev);
  }

  function showPrev(e) {
    e.stopPropagation();
    if (selectedIndex > 0) {
      setSelectedIndex((prev) => prev - 1);
      setZoomed(false);
    }
  }

  function showNext(e) {
    e.stopPropagation();
    if (selectedIndex < interiorUrls.length - 1) {
      setSelectedIndex((prev) => prev + 1);
      setZoomed(false);
    }
  }

  return (
    <div className="report-page" style={{
      width: '8.5in', minHeight: '11in', margin: '0 auto', padding: '0.3in',
      backgroundColor: '#fff', border: '1px solid #ccc', boxSizing: 'border-box',
      fontFamily: 'Arial, sans-serif', position: 'relative',
    }}>
      <h2 style={{ marginBottom: '0.2in', color: colorBlue }}>Interior Inspection</h2>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        {/* ------------- LEFT : checklist ------------- */}
        <div style={{ flex: '0 0 50%' }}>
          <div style={{ borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8 }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Interior Checklist
            </h3>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {dedupeChecklist(checklist).map((item, index) => {
                  const bgColor = index % 2 === 0 ? lightGray1 : lightGray2;
                  const clickable = ['Light', 'Moderate', 'Severe'].includes(item.DamageSeverity);

                  return (
                    <tr key={item.DropdownValueId} style={{ backgroundColor: bgColor }}>
                      <td style={{ padding: '4px 8px', verticalAlign: 'middle' }}>
                        {item.DropdownValue}
                      </td>

                      {/* clickable icon cell */}
                      <td
                        style={{
                          padding: '4px 8px',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          cursor: clickable ? 'pointer' : 'default',
                        }}
                        onClick={clickable ? scrollToDamageReport : undefined}
                        title={clickable ? 'Jump to Damage Report' : undefined}
                      >
                        {renderDamageIcon(item.DamageSeverity)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right column: Interior Images + Notes */}
        <div style={{ flex: '0 0 50%' }}>
          {/* Interior Images */}
          <div style={{ borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8, marginBottom: '0.2in' }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>Interior Images</h3>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                rowGap: '4px',
                columnGap: '8px',
                alignItems: 'flex-start',
                alignContent: 'flex-start',
              }}
            >
              {interiorUrls.map((url, idx) => (
                <div
                  key={url}
                  style={{
                    width: '80px',
                    height: '80px',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    overflow: 'hidden',
                    cursor: 'pointer',
                  }}
                  onClick={() => openModal(idx)}
                >
                  <img
                    src={url}
                    alt={`Interior${idx}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Important Interior Notes */}
          <div style={{ borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8, backgroundColor: '#f9f9f9', padding: '10px' }}>
            <h3 style={{ color: colorBlue, marginBottom: 6, fontSize: '1rem' }}>
              Important Interior Notes
            </h3>
            <p style={{ margin: '6px 0' }}>
              We call out all visible interior damage, big or small—because transparency matters.
            </p>
            <p style={{ margin: '6px 0' }}>
              Some wear is typical in used vehicles—like light seat creases, small stains, or worn buttons from regular use.
            </p>
            <p style={{ margin: '6px 0' }}>
              We strive to document everything we can see, but keep in mind: minor signs of use like these are common and may be present even if not specifically called out.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Right Logo */}
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          right: 10,
        }}
      >
        <img src="/logo.png" alt="Company Logo" style={{ height: 40 }} />
      </div>

      {/* IMAGE MODAL OVERLAY */}
      {selectedIndex !== null && interiorUrls[selectedIndex] && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <img
            src={interiorUrls[selectedIndex]}
            alt="Enlarged"
            onClick={(e) => {
              e.stopPropagation();
              toggleZoom();
            }}
            style={{
              maxWidth: zoomed ? '95%' : '50%',
              maxHeight: zoomed ? '95%' : '60%',
              border: '2px solid #fff',
              borderRadius: 6,
              cursor: zoomed ? 'zoom-out' : 'zoom-in',
              transition: 'all 0.3s ease',
            }}
          />

          {selectedIndex > 0 && (
            <div
              onClick={showPrev}
              style={{
                position: 'absolute',
                left: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                fontSize: '3rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              &#10094;
            </div>
          )}

          {selectedIndex < interiorUrls.length - 1 && (
            <div
              onClick={showNext}
              style={{
                position: 'absolute',
                right: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                fontSize: '3rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              &#10095;
            </div>
          )}

          <div
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            style={{
              position: 'absolute',
              top: 10,
              right: 20,
              color: 'white',
              fontSize: '2rem',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            &times;
          </div>
        </div>
      )}
    </div>
  );
}
